import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowDownRight, ArrowUpRight, Minus, Search, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import AdminShell from "@/components/admin/AdminShell";
import { useAuth } from "@/context/AuthContext";
import api, { apiErrorMessage } from "@/lib/api";

const ROLE_LABEL = {
  applicant: "Applicant", agency_owner: "Agency owner", agency_manager: "Agency manager",
  agency_agent: "Agency agent", reviewer: "Reviewer", administrator: "Administrator",
  super_administrator: "Super admin",
};
export const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");

function Trend({ cur, prev }) {
  if (prev === 0 && cur === 0) return <span className="flex items-center gap-1 text-xs text-muted"><Minus size={12} /> no change</span>;
  const diff = prev ? Math.round(((cur - prev) / prev) * 100) : 100;
  const up = cur >= prev;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`flex items-center gap-1 text-xs font-medium ${up ? "text-emerald-600" : "text-coral"}`}>
      <Icon size={13} />{prev ? `${diff > 0 ? "+" : ""}${diff}%` : "new"} <span className="font-normal text-muted">vs prior 7d</span>
    </span>
  );
}

export function Kpi({ label, value, sub, testId }) {
  return (
    <div className="rounded-md border border-hborder bg-white p-5" data-testid={testId}>
      <p className="text-xs font-medium uppercase tracking-wider text-muted">{label}</p>
      <p className="mt-2 font-serif text-3xl text-heading">{value}</p>
      <div className="mt-1 min-h-[1.25rem] text-xs text-muted">{sub}</div>
    </div>
  );
}

export const Skeleton = () => <div className="h-48 animate-pulse rounded-md bg-white" />;

export function AdminOverview() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/admin/overview").then((r) => setD(r.data)).catch((e) => setErr(apiErrorMessage(e.response?.data?.detail)));
  }, []);
  const k = d?.kpis;
  const maxReady = useMemo(() => Math.max(1, ...Object.values(d?.readiness || {})), [d]);

  return (
    <AdminShell title="Overview" subtitle="Platform pulse across all tenants">
      {err && <p className="rounded-md border border-coral/30 bg-white p-4 text-sm text-coral" data-testid="admin-overview-error">{err}</p>}
      {!d && !err && <Skeleton />}
      {d && (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="admin-kpi-grid">
            <Kpi testId="kpi-active-cases" label="Active cases (7d)" value={k.active_cases_7d.current} sub={<Trend cur={k.active_cases_7d.current} prev={k.active_cases_7d.previous} />} />
            <Kpi testId="kpi-new-cases" label="New cases (7d)" value={k.new_cases_7d.current} sub={<Trend cur={k.new_cases_7d.current} prev={k.new_cases_7d.previous} />} />
            <Kpi testId="kpi-ready-rate" label="Submission-ready rate" value={`${k.ready_rate.pct}%`} sub={`${k.ready_rate.ready} of ${k.ready_rate.total} cases`} />
            <Kpi testId="kpi-review-queue" label="Review queue" value={k.review_queue.depth} sub={<span className={k.review_queue.waiting_over_48h ? "font-medium text-coral" : ""}>{k.review_queue.waiting_over_48h} waiting over 48h</span>} />
            <Kpi testId="kpi-consultations" label="Consultations (7d)" value={k.consultations_7d.current} sub={<Trend cur={k.consultations_7d.current} prev={k.consultations_7d.previous} />} />
            <Kpi testId="kpi-email-rate" label="Confirmation email success" value={k.confirmation_email_rate.pct == null ? "—" : `${k.confirmation_email_rate.pct}%`} sub={`${k.confirmation_email_rate.sent} of ${k.confirmation_email_rate.total} bookings`} />
            <Kpi testId="kpi-signups" label="Signups (7d)" value={k.signups_7d.applicants + k.signups_7d.agency} sub={`${k.signups_7d.applicants} applicants · ${k.signups_7d.agency} agency`} />
            <Kpi testId="kpi-users" label="Users / Organizations" value={`${k.users_total} / ${k.organizations_total}`} sub={`${k.total_cases} cases in total`} />
          </div>

          <div className="grid gap-6 lg:grid-cols-5">
            <section className="rounded-md border border-hborder bg-white p-5 lg:col-span-2" data-testid="admin-readiness-panel">
              <h2 className="font-serif text-lg text-heading">Readiness distribution</h2>
              <div className="mt-4 space-y-3">
                {Object.entries(d.readiness).map(([state, n]) => (
                  <div key={state}>
                    <div className="flex justify-between text-sm"><span className="text-heading">{state}</span><span className="text-muted">{n}</span></div>
                    <div className="mt-1 h-2 rounded bg-softcream"><div className="h-2 rounded bg-tblue transition-all" style={{ width: `${(n / maxReady) * 100}%` }} /></div>
                  </div>
                ))}
              </div>
              <h3 className="mt-6 text-xs font-medium uppercase tracking-wider text-muted">Users by role</h3>
              <div className="mt-2 flex flex-wrap gap-2">
                {Object.entries(d.roles).map(([r, n]) => (
                  <span key={r} className="rounded border border-hborder bg-softcream px-2.5 py-1 text-xs text-heading">{ROLE_LABEL[r] || r}: <b>{n}</b></span>
                ))}
              </div>
            </section>

            <section className="rounded-md border border-hborder bg-white p-5 lg:col-span-3" data-testid="admin-activity-panel">
              <h2 className="font-serif text-lg text-heading">Recent activity</h2>
              {d.recent_activity.length === 0 ? <p className="mt-4 text-sm text-muted">No audited activity yet.</p> : (
                <ul className="mt-3 divide-y divide-hborder/70">
                  {d.recent_activity.map((a) => (
                    <li key={a.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                      <div className="min-w-0"><p className="truncate font-medium text-heading">{a.action}</p><p className="text-xs text-muted">{ROLE_LABEL[a.actor_role] || a.actor_role || "system"} · {a.source}</p></div>
                      <span className="shrink-0 text-xs text-muted">{fmt(a.created_at)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </div>
      )}
    </AdminShell>
  );
}

export function AdminUsers() {
  const { user: me } = useAuth();
  const isSuper = me?.role === "super_administrator";
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (role) params.role = role;
    api.get("/admin/users", { params }).then((r) => setUsers(r.data.users)).catch(() => setUsers([]));
  }, [q, role]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const patch = async (u, body, okMsg) => {
    setBusy(u.id);
    try {
      await api.patch(`/admin/users/${u.id}`, body);
      toast.success(okMsg);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail));
    } finally { setBusy(null); }
  };

  return (
    <AdminShell title="Users" subtitle="Search, filter and manage every account">
      <div className="mb-4 flex flex-wrap gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" data-testid="admin-users-search"
            className="w-full rounded-lg border border-hborder bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-tblue" />
        </div>
        <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="admin-users-role-filter"
          className="rounded-lg border border-hborder bg-white px-3 py-2.5 text-sm outline-none focus:border-tblue">
          <option value="">All roles</option>
          {Object.entries(ROLE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      {users === null ? <Skeleton /> : users.length === 0 ? (
        <p className="rounded-md border border-dashed border-hborder bg-white p-10 text-center text-sm text-muted" data-testid="admin-users-empty">No users match.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-hborder bg-white hv-scroll-thin">
          <table className="w-full min-w-[760px] text-left text-sm" data-testid="admin-users-table">
            <thead className="border-b border-hborder bg-softcream text-xs uppercase tracking-wider text-muted">
              <tr><th className="px-4 py-3">User</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Organization</th><th className="px-4 py-3">Joined</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const self = u.id === me?.id;
                const locked = self || (u.role === "super_administrator" && !isSuper);
                return (
                  <tr key={u.id} className="border-b border-hborder/60 last:border-0" data-testid={`admin-user-row-${u.email}`}>
                    <td className="px-4 py-3"><p className="font-medium text-heading">{u.name}</p><p className="text-xs text-muted">{u.email}</p></td>
                    <td className="px-4 py-3">
                      {isSuper && !locked ? (
                        <select value={u.role} disabled={busy === u.id} onChange={(e) => patch(u, { role: e.target.value }, `Role changed to ${ROLE_LABEL[e.target.value]}`)}
                          data-testid={`admin-user-role-${u.email}`} className="rounded-md border border-hborder bg-white px-2 py-1 text-xs">
                          {Object.entries(ROLE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                      ) : <span className="text-heading">{ROLE_LABEL[u.role] || u.role}</span>}
                    </td>
                    <td className="px-4 py-3 text-muted">{u.org_name || "—"}</td>
                    <td className="px-4 py-3 text-xs text-muted">{fmt(u.created_at)}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded border px-2.5 py-1 text-xs ${u.disabled ? "border-red-300 bg-red-50 text-red-700" : "border-emerald-300 bg-emerald-50 text-emerald-700"}`}>{u.disabled ? "Deactivated" : "Active"}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {locked ? <span className="inline-flex items-center gap-1 text-xs text-muted"><ShieldAlert size={13} />{self ? "You" : "Protected"}</span> : (
                        <button disabled={busy === u.id} data-testid={`admin-user-toggle-${u.email}`}
                          onClick={() => patch(u, { disabled: !u.disabled }, u.disabled ? "Account reactivated" : "Account deactivated")}
                          className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${u.disabled ? "border-tblue text-tblue hover:bg-ltblue" : "border-coral/40 text-coral hover:bg-coral/5"}`}>
                          {u.disabled ? "Reactivate" : "Deactivate"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {!isSuper && <p className="mt-3 text-xs text-muted">Role changes require a super administrator.</p>}
    </AdminShell>
  );
}

export function AdminOrgs() {
  const [orgs, setOrgs] = useState(null);
  useEffect(() => { api.get("/admin/organizations").then((r) => setOrgs(r.data.organizations)).catch(() => setOrgs([])); }, []);
  return (
    <AdminShell title="Organizations" subtitle="Agency tenants and their case load">
      {orgs === null ? <Skeleton /> : orgs.length === 0 ? (
        <p className="rounded-md border border-dashed border-hborder bg-white p-10 text-center text-sm text-muted">No organizations yet.</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="admin-orgs-grid">
          {orgs.map((o) => (
            <div key={o.id} className="rounded-md border border-hborder bg-white p-5" data-testid={`admin-org-${o.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div><h3 className="font-serif text-lg text-heading">{o.name}</h3><p className="text-xs text-muted">Joined {fmt(o.created_at)}</p></div>
                {o.plan && <span className="rounded bg-ltblue px-2.5 py-1 text-xs capitalize text-tblue">{o.plan}</span>}
              </div>
              <p className="mt-3 text-sm text-muted">Owner: <span className="text-heading">{o.owner ? `${o.owner.name} (${o.owner.email})` : "—"}</span></p>
              <div className="mt-4 grid grid-cols-4 gap-2 text-center">
                {[["Members", o.members], ["Cases", o.case_count], ["Ready", o.ready_cases], ["Active 7d", o.active_cases_7d]].map(([l, v]) => (
                  <div key={l} className="rounded-lg bg-softcream py-2"><p className="font-serif text-xl text-heading">{v}</p><p className="text-[10px] uppercase tracking-wide text-muted">{l}</p></div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminShell>
  );
}
